import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  type ItemChange,
  type ItemShape,
  type OrderDetail,
  type OrderItem,
  type PricingPreview,
  fetchPricingPreview,
} from '../lib/orders';
import { PRIMARY_SKU_REINIGEN, PRIMARY_SKU_WEDERVERKOPER } from '../lib/order-edit-rules';

/**
 * Lokale edit-state voor één order in edit-mode.
 *
 * Het model:
 *  - `baseline` is de server-versie waar we op begonnen
 *  - `drafts` is een Map<itemId, draftValues> met wijzigingen op bestaande items
 *  - `removed` is een Set<itemId> met te verwijderen items
 *  - `additions` is een array nieuwe items (zonder SF-id)
 *
 * `pendingChanges()` leidt af welke ItemChange[] we naar bulk-update sturen.
 * Een debounced effect ververst de server-side pricing-preview bij elke wijziging.
 */

export interface ItemDraft {
  vorm: ItemShape;
  lengteCm: number | null;
  breedteCm: number | null;
  diameterCm: number | null;
  oppervlakteM2: number | null;
  /**
   * Primary-SKU van het kleed zelf. Voor bestaande items afgeleid uit
   * `OrderItem.productCode`; voor additions ingesteld door `AddItemForm`.
   * Alleen bij `kind='add'` verstuurd naar de backend — updates mogen primary
   * niet wisselen, Apex negeert dit veld op update.
   */
  primarySku: string;
  packageSku: string | null;
  ondertapijt: boolean;
}

export interface ItemAddition extends ItemDraft {
  // Frontend-only sleutel voor React lijst-keys en voor 'remove before save'.
  tempId: string;
}

interface State {
  mode: 'read' | 'edit';
  baseline: OrderDetail | null;
  drafts: Map<string, ItemDraft>;
  removed: Set<string>;
  additions: ItemAddition[];
  preview: PricingPreview | null;
  previewLoading: boolean;
  previewError: string | null;
  isSaving: boolean;
  error: string | null;
}

type Action =
  | { type: 'enterEdit'; order: OrderDetail }
  | { type: 'exitEdit' }
  | { type: 'updateDraft'; itemId: string; patch: Partial<ItemDraft> }
  | { type: 'removeItem'; itemId: string }
  | { type: 'undoRemove'; itemId: string }
  | { type: 'addItem'; draft: ItemAddition }
  | { type: 'updateAddition'; tempId: string; patch: Partial<ItemDraft> }
  | { type: 'removeAddition'; tempId: string }
  | { type: 'setPreviewLoading'; loading: boolean }
  | { type: 'setPreview'; preview: PricingPreview | null; error?: string | null }
  | { type: 'setSaving'; saving: boolean }
  | { type: 'setError'; error: string | null };

function resolvePrimarySku(productCode: string | undefined): string {
  if (productCode === PRIMARY_SKU_REINIGEN) return PRIMARY_SKU_REINIGEN;
  if (productCode === PRIMARY_SKU_WEDERVERKOPER) return PRIMARY_SKU_WEDERVERKOPER;
  return PRIMARY_SKU_REINIGEN;
}

function itemToDraft(item: OrderItem): ItemDraft {
  const vorm: ItemShape = item.isRound ? 'rond' : item.lengthCm && item.widthCm ? 'rechthoekig' : 'overig';
  return {
    vorm,
    lengteCm: item.lengthCm,
    breedteCm: item.widthCm,
    diameterCm: item.diameterCm,
    oppervlakteM2: item.areaM2,
    primarySku: resolvePrimarySku(item.productCode),
    packageSku: null,
    ondertapijt: item.hasUnderlayment,
  };
}

const initialState: State = {
  mode: 'read',
  baseline: null,
  drafts: new Map(),
  removed: new Set(),
  additions: [],
  preview: null,
  previewLoading: false,
  previewError: null,
  isSaving: false,
  error: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'enterEdit': {
      const drafts = new Map<string, ItemDraft>();
      for (const group of action.order.items) {
        drafts.set(group.primary.id, itemToDraft(group.primary));
      }
      return {
        ...initialState,
        mode: 'edit',
        baseline: action.order,
        drafts,
      };
    }
    case 'exitEdit':
      return { ...initialState, mode: 'read', baseline: state.baseline };
    case 'updateDraft': {
      const drafts = new Map(state.drafts);
      const current = drafts.get(action.itemId);
      if (!current) return state;
      drafts.set(action.itemId, { ...current, ...action.patch });
      return { ...state, drafts };
    }
    case 'removeItem': {
      const removed = new Set(state.removed);
      removed.add(action.itemId);
      return { ...state, removed };
    }
    case 'undoRemove': {
      const removed = new Set(state.removed);
      removed.delete(action.itemId);
      return { ...state, removed };
    }
    case 'addItem':
      return { ...state, additions: [...state.additions, action.draft] };
    case 'updateAddition': {
      const additions = state.additions.map((a) =>
        a.tempId === action.tempId ? { ...a, ...action.patch } : a
      );
      return { ...state, additions };
    }
    case 'removeAddition':
      return { ...state, additions: state.additions.filter((a) => a.tempId !== action.tempId) };
    case 'setPreviewLoading':
      return { ...state, previewLoading: action.loading };
    case 'setPreview':
      return { ...state, preview: action.preview, previewError: action.error ?? null, previewLoading: false };
    case 'setSaving':
      return { ...state, isSaving: action.saving };
    case 'setError':
      return { ...state, error: action.error };
    default:
      return state;
  }
}

function draftToItemChange(draft: ItemDraft, kind: 'add' | 'update'): Partial<ItemChange> {
  return {
    vorm: draft.vorm,
    lengteCm: draft.lengteCm ?? undefined,
    breedteCm: draft.breedteCm ?? undefined,
    diameterCm: draft.diameterCm ?? undefined,
    oppervlakteM2: draft.oppervlakteM2 ?? undefined,
    // Primary-SKU alleen bij add; bij update negeert Apex dit maar voor de
    // duidelijkheid sturen we het niet mee.
    primarySku: kind === 'add' ? draft.primarySku : undefined,
    packageSku: draft.packageSku ?? undefined,
    ondertapijt: draft.ondertapijt,
  };
}

function draftDiffersFromItem(draft: ItemDraft, item: OrderItem): boolean {
  const originalVorm: ItemShape = item.isRound ? 'rond' : item.lengthCm && item.widthCm ? 'rechthoekig' : 'overig';
  return (
    draft.vorm !== originalVorm ||
    draft.lengteCm !== item.lengthCm ||
    draft.breedteCm !== item.widthCm ||
    draft.diameterCm !== item.diameterCm ||
    draft.oppervlakteM2 !== item.areaM2 ||
    draft.ondertapijt !== item.hasUnderlayment ||
    !!draft.packageSku // elke expliciete package-selectie telt als edit
  );
}

function buildChanges(state: State): ItemChange[] {
  if (!state.baseline) return [];
  const changes: ItemChange[] = [];

  for (const group of state.baseline.items) {
    const primary = group.primary;
    if (state.removed.has(primary.id)) {
      changes.push({ kind: 'remove', itemId: primary.id });
      continue;
    }
    const draft = state.drafts.get(primary.id);
    if (draft && draftDiffersFromItem(draft, primary)) {
      changes.push({ kind: 'update', itemId: primary.id, ...draftToItemChange(draft, 'update') });
    }
  }

  for (const add of state.additions) {
    changes.push({ kind: 'add', ...draftToItemChange(add, 'add') });
  }
  return changes;
}

export function useOrderEditState(order: OrderDetail | null) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enterEdit = useCallback(() => {
    if (!order) return;
    dispatch({ type: 'enterEdit', order });
  }, [order]);

  const exitEdit = useCallback(() => dispatch({ type: 'exitEdit' }), []);
  const updateDraft = useCallback((itemId: string, patch: Partial<ItemDraft>) =>
    dispatch({ type: 'updateDraft', itemId, patch }), []);
  const removeItem = useCallback((itemId: string) => dispatch({ type: 'removeItem', itemId }), []);
  const undoRemove = useCallback((itemId: string) => dispatch({ type: 'undoRemove', itemId }), []);
  const addItem = useCallback((draft: ItemAddition) => dispatch({ type: 'addItem', draft }), []);
  const updateAddition = useCallback((tempId: string, patch: Partial<ItemDraft>) =>
    dispatch({ type: 'updateAddition', tempId, patch }), []);
  const removeAddition = useCallback((tempId: string) => dispatch({ type: 'removeAddition', tempId }), []);
  const setSaving = useCallback((saving: boolean) => dispatch({ type: 'setSaving', saving }), []);
  const setError = useCallback((error: string | null) => dispatch({ type: 'setError', error }), []);

  const changes = useMemo(() => (state.mode === 'edit' ? buildChanges(state) : []), [state]);

  // Debounced pricing-preview
  useEffect(() => {
    if (state.mode !== 'edit' || !state.baseline) return;
    if (changes.length === 0) {
      dispatch({ type: 'setPreview', preview: null });
      return;
    }
    const orderId = state.baseline.id;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    dispatch({ type: 'setPreviewLoading', loading: true });
    debounceRef.current = setTimeout(() => {
      fetchPricingPreview(orderId, changes)
        .then((p) => dispatch({ type: 'setPreview', preview: p }))
        .catch((err: Error) => dispatch({ type: 'setPreview', preview: null, error: err.message }));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [changes, state.baseline, state.mode]);

  return {
    state,
    changes,
    enterEdit,
    exitEdit,
    updateDraft,
    removeItem,
    undoRemove,
    addItem,
    updateAddition,
    removeAddition,
    setSaving,
    setError,
  };
}
